import { useCallback, useId, useState } from "react";
import { FileUp, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { postExtractPdfText } from "@/lib/extract-pdf-api";
import { Button } from "@/components/ui/button";

export type PdfDropzoneMode = "stage-textarea" | "queue";

export interface PdfDropzoneProps {
  getAccessToken: () => Promise<string | null>;
  mode?: PdfDropzoneMode;
  /** stage-textarea: file staged until save; shown in dropzone */
  pendingFile?: File | null;
  onPendingFileChange?: (file: File | null) => void;
  /** stage-textarea: after extract, fills the guidelines/instructions field */
  onExtractedText?: (text: string) => void;
  /** queue: each PDF is added to a list (text stored for the review agent; does not replace the textarea) */
  onQueued?: (payload: { file: File; extractedText: string }) => void;
  fieldLabel?: string;
  helperText?: string;
  disabled?: boolean;
  className?: string;
}

export function PdfDropzone({
  getAccessToken,
  mode = "stage-textarea",
  pendingFile = null,
  onPendingFileChange,
  onExtractedText,
  onQueued,
  fieldLabel = "Reference PDF (optional)",
  helperText,
  disabled = false,
  className,
}: PdfDropzoneProps) {
  const inputId = useId();
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultHelper =
    mode === "queue"
      ? "Each PDF shows Ready or Not ready after upload. Summarize runs in the background and replaces a Not ready PDF with a short summary. Removing it cancels summarization. The text above stays unchanged."
      : "Text is copied into the box below; the file is stored when you save.";

  const processFile = useCallback(
    async (file: File | null) => {
      if (!file) {
        onPendingFileChange?.(null);
        return;
      }
      if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
        setError("Please choose a PDF file.");
        return;
      }
      setError(null);

      if (mode === "queue") {
        setIsExtracting(true);
        try {
          const { text } = await postExtractPdfText(file, getAccessToken);
          onQueued?.({ file, extractedText: text });
        } catch (e) {
          const message = e instanceof Error ? e.message : "Extraction failed";
          setError(message);
        } finally {
          setIsExtracting(false);
        }
        return;
      }

      onPendingFileChange?.(file);
      setIsExtracting(true);
      try {
        const { text } = await postExtractPdfText(file, getAccessToken);
        onExtractedText?.(text);
      } catch (e) {
        const message = e instanceof Error ? e.message : "Extraction failed";
        setError(message);
        onPendingFileChange?.(null);
      } finally {
        setIsExtracting(false);
      }
    },
    [getAccessToken, mode, onExtractedText, onPendingFileChange, onQueued]
  );

  const onInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] ?? null;
      void processFile(file);
      event.target.value = "";
    },
    [processFile]
  );

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (disabled || isExtracting) return;
      const file = event.dataTransfer.files?.[0] ?? null;
      void processFile(file);
    },
    [disabled, isExtracting, processFile]
  );

  return (
    <div className={cn("space-y-2", className)}>
      <label className="text-xs font-medium uppercase tracking-wide text-slate-400">{fieldLabel}</label>
      <div
        onDragOver={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={onDrop}
        className={cn(
          "flex min-h-[100px] cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-violet-400/35 bg-slate-950/40 px-4 py-5 text-center transition-colors hover:border-violet-400/55 hover:bg-slate-900/50",
          (disabled || isExtracting) && "pointer-events-none opacity-60"
        )}
      >
        <input
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          id={inputId}
          disabled={disabled || isExtracting}
          onChange={onInputChange}
        />
        <label htmlFor={inputId} className="flex cursor-pointer flex-col items-center gap-2">
          {isExtracting ? (
            <Loader2 className="h-8 w-8 animate-spin text-violet-300" />
          ) : (
            <FileUp className="h-8 w-8 text-violet-300/90" />
          )}
          <span className="text-sm text-slate-200">
            {isExtracting ? "Extracting text…" : "Drop a PDF here or click to browse"}
          </span>
          <span className="max-w-md text-xs text-slate-500">{helperText ?? defaultHelper}</span>
        </label>
      </div>

      {mode === "stage-textarea" && pendingFile ? (
        <div className="flex items-center justify-between gap-2 rounded-md border border-white/10 bg-slate-900/50 px-3 py-2">
          <p className="truncate text-xs text-slate-300" title={pendingFile.name}>
            Staged: {pendingFile.name}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0 text-slate-400 hover:text-white"
            disabled={disabled || isExtracting}
            onClick={() => {
              onPendingFileChange?.(null);
              setError(null);
            }}
          >
            Clear
          </Button>
        </div>
      ) : null}

      {error ? <p className="text-xs text-red-400">{error}</p> : null}
    </div>
  );
}
